import React, { useState } from "react";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import TableWithCustomSearch from "../../../../components/table/coreUiTableWithCustomSearchAndDateConversion/TableWithCustomSearch";
import { stripHTMLTagsFromText } from "../../../../utils/formatters";
import { SectionToggleWrapper } from "./SectionToggleWrapper";
import PieCharts from "./PieCharts";
import { DataPoints } from "./DataPoints";
import LogsModal from "./LogsModal";
import SalesAndAttendanceSection from "./SalesAndAttendanceSection";
import NoData from "../../../../components/noData/noData";
import { NO_FUTURE_DATA_MESSAGE } from "../../../../app_constants";

const ClientInfoContent = () => {
  const {
    clientPastDues,
    clientMembershipTypes,
    clientProspectTypes,
    clientContactLogs,
    clientCharts,
    clientContracts,
    clientCheckIns,
    clientSales,
    clientVisits,
    currentUser: { currencySymbol },
    contractStatuses,
    contractFreezes,
    remainingSessions,
    apiError,
    loadingModules
  } = useSelector((state) => {
    return state.clientProfileModule;
  });
  const [modalContent, setModalContent] = useState(null);
  const closeModal = () => {
    setModalContent(null);
  };
  const toggleModal = (content = "") => {
    setModalContent(content);
  };

  const clientInfoSections = () => {
    return (
      <>
        <div className="client-info-bottom-wrapper ">
          <span id="clientProfileModulesWrapper">
            <SalesAndAttendanceSection
              clientCharts={clientCharts}
              isLoading={loadingModules?.salesAndAttendanceData}
            />
            <SectionToggleWrapper
              title="Contracts/Agreements"
              data={clientContracts}
              fileName="contracts-agreements"
              idForConfigurator="contractsagreements"
              isLoading={loadingModules?.contractsAgreements}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Freezes"
              data={contractFreezes}
              idForConfigurator="freezes"
              fileName="freezes"
              isLoading={loadingModules?.freezes}
            />
            <SectionToggleWrapper
              title="Remaining Sessions"
              data={remainingSessions}
              idForConfigurator="remainingsessions"
              fileName="remaining-sessions"
              isLoading={loadingModules?.remainingSessions}
            />
            <SectionToggleWrapper
              title={`Membership Statuses${contractStatuses.membershipStatusLabel || ""}`}
              data={contractStatuses}
              fileName="membership-statuses"
              idForConfigurator="membershipstatuses"
              isLoading={loadingModules?.membershipStatuses}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Membership Types"
              data={clientMembershipTypes}
              fileName="membership-types"
              idForConfigurator="membershiptypes"
              isLoading={loadingModules?.membershipTypes}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Prospect Types"
              data={clientProspectTypes}
              idForConfigurator="prospectypes"
              fileName="prospect-types"
              isLoading={loadingModules?.prospectypes}
            />
            <SectionToggleWrapper
              title="Sales"
              idForConfigurator="sales"
              isLoading={loadingModules?.sales}
              data={clientSales}
              currencySymbol={currencySymbol}
              fileName="sales"
              overrideColumnCallback={(header) => ({ sorter: header !== "Exp Date" })}
              futureData={{
                label: "Future Sales",
                cssKey: "future-sales",
                data: clientSales?.futureSales,
                currencySymbol,
                noDataMessage: NO_FUTURE_DATA_MESSAGE,
                filterName: "Future Sales",
                fileName: "future-sales",
                tableProps: {
                  rawDataLink: clientSales?.futureSales?.rawDataLink,
                  overrideColumnCallback: (header) => ({ sorter: header !== "Exp Date" })
                }
              }}
            />
            <SectionToggleWrapper
              title="Past Dues"
              data={clientPastDues}
              idForConfigurator="pastdues"
              fileName="past-dues"
              isLoading={loadingModules?.pastdues}
            />
            <SectionToggleWrapper
              title="Bookings"
              idForConfigurator="bookings"
              isLoading={loadingModules?.bookings}
              data={clientVisits}
              fileName="bookings"
              futureData={{
                label: "Future Bookings",
                cssKey: "future-bookings",
                data: clientVisits?.futureBookings,
                noDataMessage: NO_FUTURE_DATA_MESSAGE,
                filterName: "Future Bookings",
                fileName: "future-bookings"
              }}
            />
            <SectionToggleWrapper
              title="Check-Ins"
              data={clientCheckIns}
              idForConfigurator="checkins"
              fileName="check-ins"
              isLoading={loadingModules?.clientCheckIns}
            />
            <SectionToggleWrapper
              title="Contact Logs"
              idForConfigurator="contactlogs"
              isLoading={loadingModules?.contactLogs}
            >
              {clientContactLogs?.tableInfo?.data.length > 0 ? (
                <>
                  <DataPoints dataPoints={clientContactLogs.dataPoints} />
                  <Tooltip id="staff-name-tooltip" />

                  <TableWithCustomSearch
                    title="Contact Logs"
                    tableWrapperId="client-contact-logs"
                    data={clientContactLogs.tableInfo.data}
                    colHeaders={clientContactLogs.tableInfo.colHeaders}
                    columns={clientContactLogs.tableInfo.columns}
                    showCount={false}
                    stripHTMLTagsInColumns={[4]}
                    rawDataLink={clientContactLogs.tableInfo.rawDataLink}
                    fileName="contact-logs"
                    overrideColumnCallback={(header) => ({
                      _style: header === "Description" ? { width: "30%" } : {}
                    })}
                    customFn={(header, formattedValue) => {
                      switch (header) {
                        case "Staff Name": {
                          let rowText = stripHTMLTagsFromText(formattedValue);
                          return (
                            <div
                              className="wrapped-text"
                              data-tooltip-content={rowText}
                              data-tooltip-id="staff-name-tooltip"
                            >
                              {rowText}
                            </div>
                          );
                        }
                        case "Description": {
                          let rowText = stripHTMLTagsFromText(formattedValue).substring(0, 100);
                          return (
                            <div className="text-ellipsis">
                              {rowText}{" "}
                              <a
                                className="contact-logs-view-message"
                                onClick={() => toggleModal(formattedValue)}
                              >
                                View
                              </a>
                            </div>
                          );
                        }
                        default:
                          return formattedValue;
                      }
                    }}
                  />
                </>
              ) : (
                <NoData />
              )}
            </SectionToggleWrapper>
            <SectionToggleWrapper
              title="Pie Charts"
              idForConfigurator="piecharts"
              isLoading={loadingModules?.pieCharts}
            >
              <PieCharts />
            </SectionToggleWrapper>
          </span>
        </div>
      </>
    );
  };

  if (apiError) {
    throw new Error("api url has error");
  }

  return (
    <>
      <LogsModal modalContent={modalContent} closeModal={closeModal} />
      {clientInfoSections()}
    </>
  );
};

export default ClientInfoContent;
